"use client";

import {FCWithHTMLAttributes} from "@/app/types/reactTypes";
import {hasAnyRole} from "@/app/util/userUtils";
import {useUserInfo} from "../contexts/userInfoContext";
import {UserRole} from "@/shared/UserRole";

/**
 * RoleRestrictedView: Can be used to mark its children as restricted to certain
 * roles, unrestricted except for certain roles, or restricted to certain roles
 * except in the presence of denied roles.
 *
 * IMPORTANT: This is a CLIENT component.  Treat this as a helper function that
 * provides guardrails for well meaning users.  Do NOT put anything sensitive
 * behind this component, as the code will be shipped to the client regardless.
 *
 * @param hiddenForRoles (optional) If defined, the component will render the fallback
 *                    element for users who have any one of these roles.
 * @param visibleForRoles If defined, the user must have one of these roles in order
 *                       for the component to render its children.
 * @param fallbackElement The element that will be rendered if the user does not pass
 *                        the permission check.
 *
 * NOTE: hiddenForRoles, if defined, take precedence.  In the case of a user who has
 *       both denied roles and permitted roles, the component will render the
 *       fallback element.
 *
 * @returns children if the user has the appropriate permissions.  Otherwise returns
 *          the fallback element.
 */
type RoleRestrictedViewProps = {
	visibleForRoles?: UserRole[];
	hiddenForRoles?: UserRole[];
	fallbackElement?: React.ReactElement;
};

const RoleRestrictedView: FCWithHTMLAttributes<RoleRestrictedViewProps> = ({
	visibleForRoles = [],
	hiddenForRoles = [],
	fallbackElement = null,
	children,
}) => {
	const {userInfo} = useUserInfo();

	if (
		(hiddenForRoles?.length > 0 && hasAnyRole(userInfo, hiddenForRoles)) ||
		(visibleForRoles?.length > 0 && !hasAnyRole(userInfo, visibleForRoles))
	) {
		return fallbackElement;
	}

	return children;
};

export default RoleRestrictedView;
